/**
 * File: PasswordField.js
 * Origin: isotect
 * Description: PasswordField component for authentication forms.
 *
 * Version: v2.0.0
 * Created: 2025-07-24
 * Last Updated: 2026-02-24
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useState } from "react";

// Local project files
import { IconButton } from "../ui/Buttons";
import IconLabel from "../ui/IconLabel";

// Icons
import { FaCheck } from "react-icons/fa";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { FaXmark } from "react-icons/fa6";

/**
 * PasswordValidationHint component renders hints under the password input field
 * to indicate which password requirements are met or missing.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {boolean} props.isValidPassword - Whether the overall password is valid
 * @param {boolean} props.correctPasswordLength - Whether the password meets the length requirement
 * @param {boolean} props.containsOneUppercase - Whether the password contains at least one uppercase letter
 * @param {boolean} props.containsOneNumber - Whether the password contains at least one number
 * @param {boolean} props.containsOneSymbol - Whether the password contains at least one symbol
 *
 * @returns {JSX.Element} A visual hint list showing password validation status
 *
 * @example
 * <PasswordValidationHint
 *   isValidPassword={false}
 *   correctPasswordLength={true}
 *   containsOneUppercase={false}
 *   containsOneNumber={true}
 *   containsOneSymbol={false}
 * />
 */
function PasswordValidationHint({
    isValidPassword,
    correctPasswordLength,
    containsOneUppercase,
    containsOneNumber,
    containsOneSymbol,
}) {
    const symbols = `!@#$%^&*`;

    return (
        <>
            <div>
                {isValidPassword ? (
                    <IconLabel icon={<FaCheck />} text={"Strong Password"} />
                ) : (
                    <IconLabel icon={<FaXmark />} text={"Weak Password"} />
                )}
                {!isValidPassword && (
                    <>
                        {correctPasswordLength ? (
                            <IconLabel
                                icon={<FaCheck />}
                                text={"Contains 8-64 characters"}
                            />
                        ) : (
                            <IconLabel
                                icon={<FaXmark />}
                                text={"Contains 8-64 characters"}
                            />
                        )}
                        {containsOneUppercase ? (
                            <IconLabel
                                icon={<FaCheck />}
                                text={"Contains at least one uppercase letter"}
                            />
                        ) : (
                            <IconLabel
                                icon={<FaXmark />}
                                text={"Contains at least one uppercase letter"}
                            />
                        )}
                        {containsOneNumber ? (
                            <IconLabel
                                icon={<FaCheck />}
                                text={"Contains at least one number"}
                            />
                        ) : (
                            <IconLabel
                                icon={<FaXmark />}
                                text={"Contains at least one number"}
                            />
                        )}
                        {containsOneSymbol ? (
                            <IconLabel
                                icon={<FaCheck />}
                                text={`Contains at least one symbol (${symbols})`}
                            />
                        ) : (
                            <IconLabel
                                icon={<FaXmark />}
                                text={`Contains at least one symbol (${symbols})`}
                            />
                        )}
                    </>
                )}
            </div>
        </>
    );
}

/**
 * PasswordField component renders a password input field with an optional
 * PasswordValidationHint component to guide the user on password requirements.
 *
 * @component
 *
 * @param {Object} props - Component properties
 * @param {string} props.label - Label text for the password field
 * @param {string} props.id - Unique ID for the input field
 * @param {string} props.placeholder - Placeholder text for the input field
 * @param {string} props.password - Current value of the password input.
 * @param {(value: string) => void} props.setPassword - Callback function triggered when the password value changes.
 * @param {boolean} [props.isValidationHint=false] - Whether to display the PasswordValidationHint below the input
 *
 * @returns {JSX.Element} A password input field with optional validation hints
 *
 * @example
 * <PasswordField
 *   label="Password"
 *   id="user-password"
 *   placeholder="Enter your password"
 *   password={password}
 *   setPassword={setPassword}
 *   isValidationHint={true}
 * />
 */
function PasswordField({
    label,
    id,
    placeholder,
    password,
    setPassword,
    isValidationHint = false,
}) {
    const [type, setType] = useState("password");
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    const [isValidPassword, setIsValidPassword] = useState(false);
    const [correctPasswordLength, setCorrectPasswordLength] = useState(false);
    const [containsOneUppercase, setContainsOneUppercase] = useState(false);
    const [containsOneNumber, setContainsOneNumber] = useState(false);
    const [containsOneSymbol, setContainsOneSymbol] = useState(false);

    const togglePasswordVisibility = () => {
        setIsPasswordVisible((prev) => !prev);
        setType(type === "password" ? "text" : "password");
    };

    const handleChange = (newPassword) => {
        setPassword(newPassword);
        if (isValidationHint) {
            validatePassword(newPassword);
        }
    };

    const validatePassword = (newPassword) => {
        const currentPasswordLength = newPassword.length;

        let validPassword = false;
        let passwordLength = false;
        let oneUppercase = false;
        let oneNumber = false;
        let oneSymbol = false;

        if (currentPasswordLength >= 8 && currentPasswordLength <= 64) {
            passwordLength = true;
        }
        for (let i = 0; i < currentPasswordLength; i++) {
            const char = newPassword.charCodeAt(i);

            if (char >= 65 && char <= 90) {
                oneUppercase = true;
            }
            if (char >= 48 && char <= 57) {
                oneNumber = true;
            }
            if (
                char == 33 ||
                (char >= 35 && char <= 38) ||
                char == 42 ||
                char == 64 ||
                char == 94
            ) {
                oneSymbol = true;
            }
        }
        if (
            passwordLength === true &&
            oneUppercase === true &&
            oneNumber === true &&
            oneSymbol === true
        ) {
            validPassword = true;
        }

        setIsValidPassword(validPassword);
        setCorrectPasswordLength(passwordLength);
        setContainsOneUppercase(oneUppercase);
        setContainsOneNumber(oneNumber);
        setContainsOneSymbol(oneSymbol);
    };

    return (
        <>
            <div className="password-field">
                <label htmlFor="password">{label}</label>
                <div className="password-field__input">
                    <input
                        type={type}
                        id={id}
                        name="password"
                        size="30"
                        minLength="8"
                        maxLength="64"
                        pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,64}"
                        required
                        placeholder={placeholder}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => handleChange(e.target.value)}
                    />
                    {isPasswordVisible ? (
                        <div className="password-field__icon">
                            <IconButton
                                icon={FaRegEyeSlash}
                                onClick={togglePasswordVisibility}
                            ></IconButton>
                        </div>
                    ) : (
                        <div className="password-field__icon">
                            <IconButton
                                icon={FaRegEye}
                                onClick={togglePasswordVisibility}
                            ></IconButton>
                        </div>
                    )}
                </div>
            </div>
            {isValidationHint && password.length > 0
                ? PasswordValidationHint({
                      isValidPassword,
                      correctPasswordLength,
                      containsOneUppercase,
                      containsOneNumber,
                      containsOneSymbol,
                  })
                : null}
        </>
    );
}

export default PasswordField;
